import {ChangeEvent, KeyboardEvent, ReactElement, RefObject} from 'react'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'

import {CloseIcon} from 'components'
import {KEY_CODES} from 'definitions/constant'
import {DopamineAssistantSendIcon} from './DopamineAssistantSendIcon'
import {
  DopamineAssistantTurn,
  DopaminePromptSubmission,
} from './DopamineAssistant.types'
import './DopamineAssistantComposer.css'

const MAX_PROMPT_LENGTH = 1000

type DopamineAssistantComposerProps = {
  composerRef: RefObject<HTMLTextAreaElement>
  disabled?: boolean
  draft: string
  isRunning: boolean
  onDraftChange(draft: string): void
  onRemoveQueued(turnId: string): void
  onSubmit(submission: DopaminePromptSubmission): void
  queuedTurn: DopamineAssistantTurn | null
}

export const DopamineAssistantComposer = ({
  composerRef,
  disabled = false,
  draft,
  isRunning,
  onDraftChange,
  onRemoveQueued,
  onSubmit,
  queuedTurn,
}: DopamineAssistantComposerProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const trimmedDraft = draft.trim()
  const canSubmit = Boolean(trimmedDraft) && !disabled
  const getSendLabel = (): string => {
    if (!isRunning) return t('SEND_MESSAGE')
    // Only one question waits; sending another takes its place, and says so.
    return queuedTurn
      ? t('DOPAMINE_REPLACE_QUEUED_MESSAGE')
      : t('DOPAMINE_QUEUE_MESSAGE')
  }
  const sendLabel = getSendLabel()
  // Idle, the tooltip carries the key hint; while a run is on it says what
  // sending does to the queue instead.
  const sendTip = isRunning ? sendLabel : t('DOPAMINE_COMPOSER_HELP')

  const submitDraft = (): void => {
    if (!canSubmit) return

    onSubmit({displayText: trimmedDraft, query: trimmedDraft})
    onDraftChange('')
  }

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    onDraftChange(event.target.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (
      event.key !== KEY_CODES.ENTER ||
      event.shiftKey ||
      event.nativeEvent.isComposing
    ) {
      return
    }

    event.preventDefault()
    submitDraft()
  }

  return (
    <div className="dopamine-composer-container shrink-0 px-4 pb-4 pt-2">
      {queuedTurn ? (
        <output
          className="dopamine-composer-queued"
          data-cy="dopamine-assistant-queued-message"
          // Remounting replays the enter animation: the row flashes on replace.
          key={queuedTurn.submission.displayText}
        >
          <span className="dopamine-composer-queued-label">
            {t('DOPAMINE_QUEUED_LABEL')}
          </span>
          <span className="dopamine-composer-queued-text">
            {queuedTurn.submission.displayText}
          </span>
          <button
            aria-label={t('DOPAMINE_REMOVE_QUEUED_MESSAGE')}
            className="dopamine-composer-queued-remove"
            data-cy="dopamine-assistant-remove-queued"
            type="button"
            onClick={() => onRemoveQueued(queuedTurn.id)}
          >
            <CloseIcon height={12} strokeColor="currentColor" width={12} />
          </button>
        </output>
      ) : null}
      <div className="dopamine-composer-field relative rounded-ds-xl border-0.5 border-white/10 px-4 py-3.5">
        <textarea
          aria-describedby="dopamine-assistant-composer-help"
          aria-label={t('ASK_DOPAMINE')}
          className="dopamine-assistant-composer-input abc-light placeholder-abc-light -mr-4 block max-h-40 min-h-[2.875rem] w-[calc(100%+1rem)] min-w-0 resize-none overflow-y-auto border-0 bg-transparent pr-14 text-sm leading-5.5 text-white caret-dopamine-accent outline-none placeholder:text-gray-500 disabled:cursor-not-allowed disabled:opacity-60"
          data-cy="dopamine-assistant-composer"
          disabled={disabled}
          maxLength={MAX_PROMPT_LENGTH}
          placeholder={t('ASK_DOPAMINE')}
          ref={composerRef}
          rows={2}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <button
          aria-disabled={!canSubmit}
          aria-label={sendLabel}
          className={classNames(
            'dopamine-send-button dopamine-tip dopamine-tip-above after:!left-auto after:!right-0 after:!translate-y-0.5 hover:after:!translate-y-0 focus-visible:after:!translate-y-0 absolute bottom-3 right-3 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-0.5 border-white/15 bg-transparent text-dopamine-muted',
            canSubmit && 'dopamine-send-button-active',
            (!canSubmit || disabled) && 'cursor-not-allowed opacity-50',
          )}
          data-cy="dopamine-assistant-send"
          data-tip={sendTip}
          disabled={disabled || !canSubmit}
          type="button"
          onClick={submitDraft}
        >
          <DopamineAssistantSendIcon />
        </button>
      </div>
      {/* The note stays visible in every state; the key hint lives in the send tooltip. */}
      <div
        className="flex flex-wrap justify-center gap-2 px-1 pt-1.5 text-center text-11px text-dopamine-muted"
        id="dopamine-assistant-composer-help"
      >
        <span>{t('DOPAMINE_COMPOSER_NOTE')}</span>
        <span className="sr-only">{t('DOPAMINE_COMPOSER_HELP')}</span>
        {draft.length >= 900 ? (
          <span>
            {draft.length}/{MAX_PROMPT_LENGTH}
          </span>
        ) : null}
      </div>
    </div>
  )
}
